// Client-side outbound friend-privacy tier logic (F-039 / D-269…D-275).
//
// Pure, DOM-free helpers for the Profile "What friends can see about me" card.
// Split out (like friendsData.js) so the tier <-> custom derivation is unit-
// testable without a component harness. The server's authoritative gate lives
// in netlify/functions/_friendVisibility.js; the tier presets here MIRROR the
// server's TIER_PRESETS — keep them in sync.
//
// Storage: notification_preferences.share_* booleans are the per-category
// truth; players.friend_privacy is the fast-path tier mirror
// (private | shared_only | full | custom). See docs/friends-privacy.md.

// The 8 gateable share columns, in display order, with plain-language copy and
// which tier first exposes each. name/avatar is intentionally absent — it is
// always visible to a friend and has no toggle.
export const SHARE_CATEGORY_META = [
  { key: 'share_index', label: 'TournyFlex Index', desc: 'Your Overall + Attested index and recent-form trend', tier: 'shared' },
  { key: 'share_rounds_count', label: 'Rounds played', desc: 'How many rounds you’ve logged', tier: 'shared' },
  { key: 'share_completed_rounds', label: 'Finished rounds', desc: 'Course + final score when you finish a round', tier: 'shared' },
  { key: 'share_live_round', label: 'Live round', desc: 'That you’re playing now, so friends can follow along', tier: 'shared' },
  { key: 'share_event_participation', label: 'Events & leagues', desc: 'That you joined an outing, event, or league', tier: 'shared' },
  { key: 'share_scorecards', label: 'Hole-by-hole scorecards', desc: 'Let friends browse your past scorecards', tier: 'full' },
  { key: 'share_detailed_stats', label: 'Detailed stats', desc: 'Fairways, greens, and putts', tier: 'full' },
  { key: 'share_side_games', label: 'Side-game results', desc: 'How you did in skins and other side games', tier: 'full' },
]

export const SHARE_KEYS = SHARE_CATEGORY_META.map((c) => c.key)

// Mirror of _friendVisibility.js TIER_PRESETS (server source of truth).
export const TIER_PRESETS = {
  private: {
    share_index: false, share_rounds_count: false, share_completed_rounds: false,
    share_live_round: false, share_event_participation: false,
    share_scorecards: false, share_detailed_stats: false, share_side_games: false,
  },
  shared_only: {
    share_index: true, share_rounds_count: true, share_completed_rounds: true,
    share_live_round: true, share_event_participation: true,
    share_scorecards: false, share_detailed_stats: false, share_side_games: false,
  },
  full: {
    share_index: true, share_rounds_count: true, share_completed_rounds: true,
    share_live_round: true, share_event_participation: true,
    share_scorecards: true, share_detailed_stats: true, share_side_games: true,
  },
}

// The three fast-path tiers surfaced as buttons (custom is derived, never a
// button).
export const TIER_OPTIONS = [
  { value: 'private', label: 'Private', blurb: 'Only your name and photo.' },
  { value: 'shared_only', label: 'Shared', blurb: 'Index, rounds, activity, and events. Not scorecards or stats.' },
  { value: 'full', label: 'Full', blurb: 'Everything above, plus scorecards, stats, and side-game results.' },
]

// Return the 8-boolean preset for a tier (unknown/custom/null → full, the
// D-340 default). Mirrors the server DEFAULT_TIER in _friendVisibility.js.
export function tierPreset(tier) {
  return { ...(TIER_PRESETS[tier] || TIER_PRESETS.full) }
}

// Given the 8 share booleans, derive the tier label. Exact match to a preset
// returns that tier; anything else is 'custom'.
export function deriveTier(shareObj) {
  const s = shareObj || {}
  for (const tier of ['private', 'shared_only', 'full']) {
    const preset = TIER_PRESETS[tier]
    if (SHARE_KEYS.every((k) => Boolean(s[k]) === preset[k])) return tier
  }
  return 'custom'
}

// Extract just the 8 share booleans from a prefs object (fills missing keys
// from the `full` default (D-340) so a partial payload is well-formed).
export function shareBooleans(prefs) {
  const base = TIER_PRESETS.full
  const out = {}
  for (const k of SHARE_KEYS) out[k] = typeof prefs?.[k] === 'boolean' ? prefs[k] : base[k]
  return out
}

// Build the ONLY fields the friend-privacy card writes to
// notification_preferences: the 8 share_* booleans plus the friend_privacy
// tier mirror. Notification flags (push_enabled, round_invites, …) are
// deliberately excluded so a friend-privacy save never rewrites them with a
// stale snapshot — the sibling Notifications card owns those (F-039 two-writer
// review). Pure + exported for unit testing.
export function friendPrivacyPayload(share, tier) {
  const out = {}
  for (const k of SHARE_KEYS) out[k] = Boolean(share?.[k])
  out.friend_privacy = tier
  return out
}
